import { FC, useRef, useState, useEffect } from 'react';
import { Play, Pause, AlertCircle, Loader2 } from 'lucide-react';
import {
  useCallRecording,
  useLiveTransferRecording,
  type LiveTransferRecordingKind,
} from '../../hooks/useCallRecording';
import { cn } from '../../utils/cn';

type CallRecordingPlayerProps =
  | {
      /** Call id used by the proxy endpoint (`/api/v1/calls/{call_id}/recording`). */
      callId: string;
      transferId?: never;
      kind?: never;
      /** Set true when the row has a recording URL (so we skip the fetch for calls without one). */
      hasRecording?: boolean;
      className?: string;
    }
  | {
      callId?: never;
      /** Live-transfer id used by `/api/v1/live-transfers/{transfer_id}/recording` (or /voicemail). */
      transferId: string;
      /** Which leg to play — defaults to 'recording'. */
      kind?: LiveTransferRecordingKind;
      hasRecording?: boolean;
      className?: string;
    };

const fmt = (s: number): string => {
  if (!isFinite(s) || s < 0) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
};

/**
 * Sleek custom call-recording player. Fetches the audio via the authed
 * `/api/v1/calls/{call_id}/recording` proxy, then renders a play/pause
 * button, animated equalizer bars while playing, time readout, and a
 * scrub bar — instead of relying on the default native `<audio>` controls.
 */
export const CallRecordingPlayer: FC<CallRecordingPlayerProps> = (props) => {
  const { hasRecording = true, className } = props;
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  // Two parallel hooks; only one is `enabled` based on which id was provided.
  const callQuery = useCallRecording(
    'callId' in props && hasRecording ? props.callId : null,
    { enabled: 'callId' in props && hasRecording },
  );
  const transferQuery = useLiveTransferRecording(
    'transferId' in props && hasRecording ? props.transferId : null,
    'transferId' in props ? (props.kind ?? 'recording') : 'recording',
    { enabled: 'transferId' in props && hasRecording },
  );
  const { data: src, isLoading, error } =
    'transferId' in props ? transferQuery : callQuery;

  // Keep React state in sync with media events
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onEnded = () => setIsPlaying(false);
    const onTime = () => setCurrentTime(el.currentTime);
    const onLoaded = () => setDuration(el.duration || 0);
    el.addEventListener('play', onPlay);
    el.addEventListener('pause', onPause);
    el.addEventListener('ended', onEnded);
    el.addEventListener('timeupdate', onTime);
    el.addEventListener('loadedmetadata', onLoaded);
    return () => {
      el.removeEventListener('play', onPlay);
      el.removeEventListener('pause', onPause);
      el.removeEventListener('ended', onEnded);
      el.removeEventListener('timeupdate', onTime);
      el.removeEventListener('loadedmetadata', onLoaded);
    };
  }, [src]);

  const toggle = () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) el.play();
    else el.pause();
  };

  const onScrub = (e: React.ChangeEvent<HTMLInputElement>) => {
    const el = audioRef.current;
    if (!el) return;
    const next = Number(e.target.value);
    el.currentTime = next;
    setCurrentTime(next);
  };

  const pct = duration > 0 ? (currentTime / duration) * 100 : 0;

  if (isLoading) {
    return (
      <div className={cn('inline-flex items-center gap-2 text-xs text-muted-foreground', className)}>
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Loading recording…
      </div>
    );
  }

  if (error || !src) {
    return (
      <div
        className={cn('inline-flex items-center gap-1.5 text-xs text-destructive', className)}
        title={error?.message}
      >
        <AlertCircle className="h-3.5 w-3.5" />
        Recording unavailable
      </div>
    );
  }

  return (
    <div
      className={cn(
        'inline-flex items-center gap-2 w-full max-w-xs',
        'px-2 py-1 rounded-full border border-foreground/20',
        'bg-gradient-to-r from-primary/15 via-foreground/[0.04] to-transparent',
        'dark:from-primary/25 dark:via-white/[0.06] dark:to-white/[0.02]',
        className
      )}
    >
      <audio ref={audioRef} src={src} preload="metadata" />

      {/* Play / pause */}
      <button
        type="button"
        onClick={toggle}
        aria-label={isPlaying ? 'Pause' : 'Play'}
        className={cn(
          'shrink-0 h-6 w-6 rounded-full flex items-center justify-center',
          'bg-primary text-primary-foreground shadow-sm',
          'hover:scale-105 active:scale-95 transition-transform'
        )}
      >
        {isPlaying ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3 ml-0.5" />}
      </button>

      {/* Scrub + time */}
      <div className="flex-1 min-w-0 flex items-center gap-1.5">
        <input
          type="range"
          min={0}
          max={duration || 0}
          step={0.1}
          value={currentTime}
          onChange={onScrub}
          className="recording-scrub flex-1 min-w-0"
          style={{ '--pct': `${pct}%` } as React.CSSProperties}
        />
        <span className="text-[10px] font-mono text-muted-foreground shrink-0 tabular-nums">
          {fmt(currentTime)} / {fmt(duration)}
        </span>
      </div>
    </div>
  );
};
